import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const Results = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedExam, setSelectedExam] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/admin/api/results.php');
        const data = await res.json();
        if (Array.isArray(data)) setResults(data);
        else if (data.data) setResults(data.data);
      } catch { /* silent */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const uniqueGrades = [...new Set(results.map(r => r.grade).filter(Boolean))];
  const uniqueExams = [...new Set(results.map(r => r.exam_type).filter(Boolean))];

  const filtered = results.filter(r => {
    const g = selectedGrade === 'all' || r.grade === selectedGrade;
    const e = selectedExam === 'all' || r.exam_type === selectedExam;
    return g && e;
  });

  const fmtDate = (s) => new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="page-enter">
      <Helmet>
        <title>Results — PAK School &amp; College System | Academic Announcements</title>
        <meta name="description" content="Access academic results, exam scores, and announcements from PAK School & College System Mardan." />
        <link rel="canonical" href="https://pakschoolandcollege.com/results" />
      </Helmet>

      <div className="page-hero">
        <div className="page-hero-content">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
            📊 Academic Performance
          </div>
          <h1>Results &amp; <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Announcements</span></h1>
          <p>Access your academic results, exam scores, and important school announcements</p>
        </div>
      </div>

      <div className="py-14 bg-white min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Filters */}
          {results.length > 0 && (
            <div className="bg-gray-50 rounded-2xl p-5 mb-8 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-navy font-poppins font-600 text-sm mb-2">Filter by Grade</label>
                <select value={selectedGrade} onChange={e => setSelectedGrade(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-inter focus:outline-none focus:border-primary bg-white">
                  <option value="all">All Grades</option>
                  {uniqueGrades.map(g => <option key={g} value={g}>Grade {g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-navy font-poppins font-600 text-sm mb-2">Filter by Exam Type</label>
                <select value={selectedExam} onChange={e => setSelectedExam(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-inter focus:outline-none focus:border-primary bg-white">
                  <option value="all">All Exam Types</option>
                  {uniqueExams.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
            </div>
          )}

          {loading && <div className="flex flex-col items-center py-24 gap-4"><div className="spinner" /><p className="text-gray-400 text-sm font-inter">Loading results...</p></div>}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-5">
                <i className="fas fa-chart-bar text-3xl text-gray-300" />
              </div>
              <h3 className="font-poppins font-700 text-gray-700 text-xl mb-2">No Results Yet</h3>
              <p className="text-gray-400 font-inter text-sm">Results will be published here as soon as they become available.</p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((result) => (
                <div key={result.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                  <div className={`h-1.5 ${result.publish_status === 'published' ? 'bg-gradient-to-r from-primary to-accent' : 'bg-gray-200'}`} />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`text-xs font-poppins font-700 px-2.5 py-1 rounded-full ${result.publish_status === 'published' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-amber-100 text-amber-700 border border-amber-200'}`}>
                        {result.publish_status === 'published' ? 'Published' : 'Draft'}
                      </span>
                      <span className="text-xs text-gray-400 font-inter">{fmtDate(result.created_at)}</span>
                    </div>
                    <h3 className="font-poppins font-700 text-navy text-base mb-3">{result.title}</h3>
                    <div className="space-y-1.5 mb-3">
                      <div className="flex items-center gap-2 text-sm text-gray-500 font-inter"><i className="fas fa-graduation-cap text-primary w-4 text-center" />Grade {result.grade}</div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 font-inter"><i className="fas fa-clipboard-list text-accent w-4 text-center" />{result.exam_type}</div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 font-inter"><i className="fas fa-calendar text-green-500 w-4 text-center" />AY: {result.academic_year}</div>
                    </div>
                    {result.description && <p className="text-gray-400 text-xs font-inter leading-relaxed line-clamp-2 mb-4">{result.description}</p>}
                    <div className="flex gap-2">
                      {result.file_path && (
                        <a href={`admin/${result.file_path}`} target="_blank" rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-accent text-white text-xs font-poppins font-600 rounded-xl hover:bg-accent-dark transition-colors">
                          <i className="fas fa-download" />Download
                        </a>
                      )}
                      <button className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary text-white text-xs font-poppins font-600 rounded-xl hover:bg-primary-dark transition-colors">
                        <i className="fas fa-eye" />View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Notice */}
          <div className="mt-10 bg-primary/5 border border-primary/20 rounded-2xl p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-info-circle text-primary" />
            </div>
            <div>
              <h4 className="font-poppins font-700 text-navy text-sm mb-1">Important Notice</h4>
              <p className="text-gray-500 font-inter text-sm leading-relaxed">Results are published as soon as they are available. For inquiries, please contact the school administration. All results are official and can be used for academic purposes.</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Results;
